import { DateRangePickerPresetKey, getDateRangePickerPresetValue } from '@shigo-ui/ui';
import { DashboardDatePreset, type DashboardDateRange } from './model';

const dashboardPickerPresetByPreset: Record<DashboardDatePreset, DateRangePickerPresetKey> = {
  [DashboardDatePreset.Today]: DateRangePickerPresetKey.Today,
  [DashboardDatePreset.Yesterday]: DateRangePickerPresetKey.Yesterday,
  [DashboardDatePreset.ThisWeek]: DateRangePickerPresetKey.ThisWeek,
  [DashboardDatePreset.Last7Days]: DateRangePickerPresetKey.Last7Days,
  [DashboardDatePreset.ThisMonth]: DateRangePickerPresetKey.ThisMonth,
  [DashboardDatePreset.Last30Days]: DateRangePickerPresetKey.Last30Days,
  [DashboardDatePreset.LastMonth]: DateRangePickerPresetKey.LastMonth,
  [DashboardDatePreset.Last3Months]: DateRangePickerPresetKey.Last3Months,
};

const datePattern = /^\d{4}-\d{2}-\d{2}$/;

export function dashboardRangeForPreset(
  preset: DashboardDatePreset,
  now = new Date(),
): DashboardDateRange {
  const resolved = getDateRangePickerPresetValue(
    dashboardPickerPresetByPreset[preset],
    dashboardMaxDate(now),
    { endPeriodsYesterday: preset === DashboardDatePreset.Last30Days },
  );
  if (resolved.dateFrom > resolved.dateTo)
    return dashboardRangeForPreset(DashboardDatePreset.Yesterday, now);
  return {
    dateFrom: resolved.dateFrom,
    dateTo: resolved.dateTo,
    preset,
  };
}

export function normalizeDashboardCustomRange(
  dateFrom: string,
  dateTo: string,
  now = new Date(),
): DashboardDateRange | null {
  const from = parseDashboardDate(dateFrom);
  const to = parseDashboardDate(dateTo);
  const today = dashboardMaxDate(now);
  if (!from || !to || from > to || to > today) return null;
  return { dateFrom: formatDashboardDate(from), dateTo: formatDashboardDate(to) };
}

function parseDashboardDate(value: string) {
  if (!datePattern.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

function formatDashboardDate(date: Date) {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// The picker stores calendar dates in local Date fields, never as reporting instants.
// Project the UTC reporting day into that date-only representation at its boundary.
export function dashboardMaxDate(date = new Date()) {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}
